/** English strings for the app's publish area. Keys are camelCase, grouped by screen. */
export default {
  /** The Publish popover (or bottom sheet on a phone). */
  publishDialog: {
    title: "Publish",
    subtitle: "Put your app on a public URL.",
    docs: "Docs",
    unpublish: "Unpublish",
    publishButton: "Publish",
    publishChanges: "Publish changes",
    notOnPlan: "Publishing isn’t available on your plan.",
    notOnPlanHint: "Claim 30% off Pro for more credits and higher publish limits.",
    unpublishingTitle: "Taking your site offline",
    unpublishingUrl: "<url>{host}</url> will stop working in a moment. You can publish again any time.",
    unpublishingNoUrl: "Your public URL will stop working in a moment. You can publish again any time.",
    publishingTitle: "Publishing your project",
    publishingHint: "This can take a minute. You can close this and keep building.",
    progressStep: "{step}…",
    published: "Published",
    hostingCreditsLeft: "{credits} credits left for hosting",
    lowCredits: "Low credits — published apps pause when the balance hits zero.",
    buyCredits: "Buy credits",
    siteUrl: "Site URL",
    website: "Website",
    addDomain: "Add domain",
    surfaceActions: "{label} actions",
    domainActions: "Actions for {host}",
    manageDomain: "Manage domain",
    verifyDns: "Verify DNS",
    setPrimary: "Set as primary",
    primary: "Primary",
    waitingForDns: "Waiting for DNS",
    yourDomains: "Your domains",
    onAnotherProject: "On another project",
    useHere: "Use here",
    paused: "This app is paused — credits ran out or hosting was suspended.",
    pausedCloudFree: "Paused — this month's Cloud credits ran out. It's back on the 1st, or upgrade to bring it back now.",
    pausedCreditsPro: "Paused — you're out of credits. Buy credits to bring it back.",
    upgradeToPro: "Upgrade to Pro",
    raiseLimit: "Raise the limit",
    pausedLimit: "Paused — this app reached its monthly limit. Raise it in Settings → Usage, or it's back on the 1st.",
    pausedRepublish: "This app is paused. Publish again to bring it online.",
    cantRepublish: "Can’t republish yet",
    notReady: "Not ready to publish yet",
    askAiFinishSetup: "Ask AI to finish setup",
    askAiToPublish: "Ask AI to publish",
    fixWithAi: "Fix with AI",
    subdomain: "Subdomain",
    verifyEmail: "Verify your email first — <link>enter your code here</link>.",
    outOfCredits: "You’re out of credits.",
    outOfCreditsHint:
      "Published apps pause when the balance hits zero. Claim 30% off Pro for more credits, or wait for your daily grant.",
    unpublishConfirmTitle: "Unpublish?",
    unpublishConfirmBody: "The public URL will stop working until you publish again.",
    republishConfirmTitle: "Publish changes?",
    republishConfirmBody:
      "Replaces the live app with this workspace’s current version. Brief downtime is possible.",
    liveDbTitle: "This publish deletes data in your live app",
    liveDbBody: "Your live app has its own database. To match development, these changes will be made to it, and the data they remove can’t be brought back:",
    liveDbConfirm: "Publish and delete this data",
    liveDbMore: "and {n} more",
    offlineToast: "Your site is offline. Publish again any time.",
    unpublishFailed: "Couldn't unpublish. Try again.",
    slugTaken: "That link name is taken — try another.",
    deploymentLimit: "You've used all published apps on your plan.",
    genericFail: "Publish didn’t finish.",
    failNameRejected: "That link name can’t be used — pick another",
    failRetryLater: "Publish didn’t go out — try again in a few minutes",
    failFixWithAi: "Publish failed — use Fix with AI",
    publishFirst: "Publish this project first.",
    couldntConnect: "Couldn't connect.",
    couldntRemoveDomain: "Couldn't remove that domain.",
    couldntSetPrimary: "Couldn't set primary domain.",
    dnsNotReady: "DNS not ready yet — check the CNAME and try again.",
    /** Sent into the person's chat as their message to the agent. */
    agentFixPrompt:
      'Publish failed for slug "{slug}": {error}\n\nFix whatever is blocking the publish, then publish with that slug.',
    agentFinishSetupPrompt: 'Finish publish setup, then publish to slug "{slug}".\n\nBlocked because:\n{reason}',
    agentPublishPrompt:
      "Publish this app to slug \"{slug}\". Set up production deploy if it isn't ready yet, then publish.",
  },
  /** The soft progress steps while a publish runs, and a PublishApp card's outcome. */
  progress: {
    preparing: "Preparing",
    building: "Building",
    publishing: "Publishing",
    goingLive: "Going live",
    stillPublishing: "Still publishing.",
    slugTaken: "That link name is taken",
    timedOut: "Publish timed out or was interrupted. Try again.",
  },
  /** Errors the agent connection shows. */
  agentConnection: {
    connectionLost: "Couldn't reach Mythex. Check your connection and try again.",
    buildInProgress: "Another build is still finishing. Wait a moment, then try again.",
    sandboxNotRunning: "The workspace isn't ready yet. Try again in a moment.",
    couldntStart: "Couldn't start the agent ({status}). Try again.",
  },
  /** The Connectors modal and the dashboard pill. */
  connectors: {
    title: "Connectors",
    heroTitle: "Build from what you already use",
    heroBody:
      "Connectors let your Mythex app talk to tools like Google Sheets, Slack and Stripe. Connect one, then just ask the agent.",
    viewDocs: "View the docs ↗",
    gotIt: "Got it",
    connectApp: "Connect {name}.",
    /** The rail's categories, by the id the API sends (apps/api/src/lib/composio.ts CONNECTOR_CATEGORIES). */
    categoryNames: {
      productivity: "Productivity",
      communication: "Communication",
      email: "Email",
      crm: "CRM",
      "project-management": "Project management",
      "developer-tools": "Developer tools",
      analytics: "Analytics",
      marketing: "Marketing",
      ecommerce: "E-commerce",
      "payment-processing": "Payments",
      "customer-support": "Support",
      accounting: "Accounting",
    },
    opening: "Opening…",
    couldntStart: "Couldn't start that connection.",
    disconnected: "Disconnected.",
    couldntDisconnect: "Couldn't disconnect that app.",
    search: "Search connectors",
    notEnabled: "Connectors aren't enabled here yet.",
    loadFailed: "Couldn't load your connectors.",
    nothingConnected: "Nothing connected yet.",
    browse: "Browse connectors",
    catalogFailed: "Couldn't load the connector catalog.",
    noConnectedMatch: "None of your connected apps match that search.",
    noMatch: "No connectors match that search.",
    loadingMore: "Loading",
    loadMore: "Load more",
    mcpFooterTitle: "Not here? Add your own MCP server",
    mcpFooterBody: "Connect any MCP server by URL — yours, your company's, or a vendor's.",
    customMcp: "Custom MCP",
    all: "All",
    categories: "Categories",
    breadcrumb: "Breadcrumb",
    addMcpServer: "Add MCP server",
    editMcpServer: "Edit MCP server",
    filter: "Filter connectors",
    pillLabel: "Connect all your tools",
  },
  /** The in-app support messenger. */
  support: {
    dialogLabel: "Support chat",
    title: "Mythex support",
    headerWaiting: "A teammate will reply here",
    headerHuman: "Chatting with a teammate",
    headerBot: "Mythex AI · a teammate if needed",
    pastConversations: "Past conversations",
    endConversation: "End this conversation",
    end: "End",
    newConversation: "New conversation",
    closeSupport: "Close support",
    yourConversations: "Your conversations",
    backToChat: "Back to chat",
    noPast: "No past conversations yet.",
    youPrefix: "You: {text}",
    noMessages: "No messages yet",
    ended: "Ended",
    teammate: "Teammate",
    earlierMessages: "Earlier messages",
    greeting: "Hi — I'm Mythex AI. How can we help?",
    writing: "Mythex AI is writing",
    handoff: "You'll get a reply from a teammate in this chat.",
    unavailable: "Chat is unavailable. Email {email}.",
    rateLimited: "Too many messages right now. Try again in a minute, or email {email}.",
    sendFailed: "Couldn't send. Try again.",
    placeholder: "Message…",
    messageLabel: "Message",
    justNow: "Just now",
    minutesAgo: "{n}m ago",
    hoursAgo: "{n}h ago",
    yesterday: "Yesterday",
    daysAgo: "{n}d ago",
  },
  /** Phone apps: the chat card when the AI builds, the hint under Publish, and the App Store account in Account settings. */
  mobile: {
    titleAndroid: "Android app",
    titleIos: "iPhone app",
    /** While it builds: the stage it's on (the build machine's real steps), then "Step 1 of 2". */
    stages: {
      android: ["Preparing", "Building and signing the app"],
      ios: ["Preparing", "Building the app", "Signing and sending to TestFlight"],
      /** Android with Google Play connected. */
      play: ["Preparing", "Building and signing the app", "Sending to Google Play"],
    },
    stageOf: "Step {n} of {total}",
    takes: { android: "2–5 min", ios: "3–10 min", play: "3–6 min" },
    waitingAccount: "Waiting for App Store account",
    ready: "Ready to upload",
    sentIos: "In TestFlight",
    sentAndroid: "In internal testing",
    playConsole: "Play Console",
    download: "Download",
    testFlight: "TestFlight",
    /** The status line when a build didn't make it, by its errorCode. */
    statusFor: {
      build: "Build failed",
      app_missing: "Create the app in App Store",
      store_key: "Reconnect App Store",
      version_released: "Needs a new version",
      bundle_taken: "Needs a new app ID",
      store_needed: "Not connected to App Store",
      owner_store_needed: "Owner needs to connect App Store",
      play_app_missing: "Create the app in Google Play",
      play_access: "Give access in Play Console",
      play_refused: "Google Play refused the build",
      store_key_play: "Reconnect Google Play",
      upgrade_required: "Needs the Pro plan",
      out_of_credits: "Out of credits",
      our_side: "Build stopped",
    },
    /** The line under it, inside the card ({name}, {version} are the app's; {link} is App Store Connect). */
    problems: {
      build: "Something in the app's code stopped the build. Ask the AI to fix it.",
      app_missing: "Add “{name}” as a new app in {link}, then ask the AI to build again.",
      store_key: "App Store Connect didn't accept the key. Ask the AI to build again to connect a new one.",
      version_released: "Version {version} is already on the App Store. Ask the AI for a new version.",
      bundle_taken: "Another Apple developer already uses this app's ID. Ask the AI for a new one.",
      store_needed: "Ask the AI to build for iPhone to connect it.",
      owner_store_needed: "Only the project's owner can connect it.",
      play_app_missing: "Add “{name}” as a new app in {link}, then ask the AI to send it again.",
      play_access: "The connected key can't manage this app. Invite its email in Play Console → Users and permissions, then ask the AI to send it again.",
      play_refused: "Google Play didn't take this build. Ask the AI to fix it.",
      store_key_play: "Google didn't accept the key anymore. Ask the AI to connect a new one.",
      upgrade_required: "Phone apps are part of the Pro plan. Upgrade, then ask the AI to build again.",
      out_of_credits: "A build is paid for in credits. Add credits, then ask the AI to build again.",
      our_side: "The build stopped on our side. Ask the AI to try again.",
    },
    appStoreConnect: "App Store Connect",
    /** Project settings: the App Store account this project's iPhone builds use. */
    settingsTab: "Mobile app",
    androidFile: {
      body: "The file to upload in Google Play Console. Ask the AI in the chat to build a new one.",
      none: "Not built yet",
      building: "Building",
      expired: "Ready, but the file is no longer kept. Ask the AI to build again.",
    },
    account: {
      title: "App Store account",
      body: "This project's iPhone builds are signed with it and sent to its TestFlight. Connect it here, or the chat asks for it the first time you build for iPhone.",
      connected: "Connected · {label}",
      notConnected: "Not connected",
      ownerOnly: "Only the project's owner can change it.",
      proOnly: "Phone apps are part of the Pro plan: upgrade to build and connect stores. A connected account stays saved.",
    },
    /** The Publish popover's Android and iPhone tabs. */
    publish: {
      tabs: { website: "Website", android: "Android", ios: "iPhone" },
      tabsLabel: "Publish to",
      pitch: { android: "Build it as an app for Android phones", ios: "Build it as an app for iPhone" },
      notBuilt: "Not built yet",
      building: "Building",
      withBuild: "{status} · build {n}",
      googlePlay: "Google Play",
      appStore: "App Store",
      googlePlayNotConnected: "Not connected — the file is to download",
      appStoreNotConnected: "Not connected — asked for on the first build",
      manage: "Manage",
      connect: "Connect",
      build: { android: "Build Android app", ios: "Build iPhone app" },
      buildAgain: "Build new version",
      ask: {
        android: "Build my app for Android.",
        ios: "Build my app for iPhone.",
        androidAgain: "Build a new version of my Android app with the latest changes.",
        iosAgain: "Build a new version of my iPhone app with the latest changes.",
      },
      notOnPlan: "Phone apps aren’t on your plan.",
      notOnPlanHint: "Claim 30% off Pro to build Android and iPhone apps.",
    },
    /** Project settings: the Google Play account this project's Android builds go to. */
    playAccount: {
      title: "Google Play account",
      body: "This project's Android builds go to the app's internal testing in Google Play. Connect it here, or ask the AI to put the app on Google Play.",
    },
    /** The panel when the AI needs the Google Play account. */
    play: {
      title: "Connect Google Play",
      why: "This project's Android builds go to the app's internal testing in Google Play.",
      step1: "In Google Cloud, create a service account and a JSON key for it.",
      step2: "In Play Console → Users and permissions, invite its email with access to this app (releases and store listing).",
      step3: "Drop the key here.",
      dropKey: "Drop the .json key here, or choose it",
      howTo: "Step-by-step guide",
    },
    /** The panel above the message box when the AI needs the App Store account. */
    stores: {
      title: "Connect App Store",
      why: "This project's iPhone builds are signed with an App Store account and sent to its TestFlight.",
      reuse: "Use {label} (from {project})",
      orNewKey: "Or connect a different key:",
      disconnect: "Disconnect",
      cancel: "Cancel",
      save: "Connect",
      checkingKey: "Checking",
      connectFailed: "Couldn't connect. Try again in a moment.",
      issuerId: "Issuer ID",
      keyId: "Key ID",
      dropKey: "Drop your .p8 key here, or choose it",
      howTo: "Create a key in App Store Connect (Admin role)",
    },
  },
};
